import { useQuery } from "@tanstack/react-query";
import { erc20Abi } from "viem";
import { createReadClient } from "../lib/chainClient";
import { usePublicConfig } from "./usePublicConfig";

export interface WalletBalances {
  usdcBaseUnits: bigint;
  ethWeiForGas: bigint;
}

/**
 * Best-effort, read-only balance display (USDC + native ETH for gas). This is purely
 * informational — it is never used to infer escrow/funding state, which always comes from
 * the API's order records.
 */
export function useWalletBalances(address: `0x${string}` | null) {
  const { data: config } = usePublicConfig();

  return useQuery({
    queryKey: [
      "wallet-balances",
      address,
      config?.chainId,
      config?.rpcUrl,
      config?.usdcContractAddress,
    ],
    queryFn: async (): Promise<WalletBalances> => {
      if (!address || !config) throw new Error("wallet address and public config are required");
      const client = createReadClient(config.chainId, config.rpcUrl);
      const [usdcBaseUnits, ethWeiForGas] = await Promise.all([
        client.readContract({
          address: config.usdcContractAddress,
          abi: erc20Abi,
          functionName: "balanceOf",
          args: [address],
        }),
        client.getBalance({ address }),
      ]);
      return { usdcBaseUnits, ethWeiForGas };
    },
    enabled: Boolean(address && config),
    staleTime: 20_000,
    refetchInterval: 30_000,
  });
}
